import type { AddMarketExportBody, MarketExportView } from "@slfinrtl/ui-bff-routes"
import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { defineMessages, useIntl } from "react-intl"
import { trackEvent } from "src/components/adobeAnalytics"
import { Modal } from "src/context/modal/Modal"
import { ModalContext } from "src/context/modal/ModalContext"
import type { ModalProps } from "src/context/modal/ModalProvider"
import type { ExportTimeFrame } from "../common/exportTimeFrame"
import { marketExportMessages } from "../common/marketExport.messages"
import { MarketExportModalContent } from "./MarketExportModalContent"

const messages = defineMessages({
	marketExportError: {
		defaultMessage: "An error occurred while exporting the market data.",
		id: "marketExportError",
	},
})

const TIME_FRAME_TRACKING_VALUES: Record<ExportTimeFrame, string> = {
	currentMonth: "current_month",
	customSelection: "custom",
	lastMonth: "last_month",
}

export interface MarketExportModalContainerProps {
	marketplaceKey: string
	onExport: (
		request: AddMarketExportBody,
		onProgress: (progress: number | undefined) => void,
		signal: AbortSignal,
	) => Promise<void>
	view: MarketExportView
}

type Props = ModalProps<MarketExportModalContainerProps>

export const MarketExportModalContainer = ({ id, modalProps: { marketplaceKey, onExport, view } }: Props) => {
	const [isLoading, setIsLoading] = useState(false)
	const [progress, setProgress] = useState<number>()
	const [today] = useState(() => new Date())
	const { hideModal, isOpen } = useContext(ModalContext)
	const { formatMessage } = useIntl()
	const [error, setError] = useState<string | null>(null)
	const exportController = useRef<AbortController | null>(null)
	const isModalOpen = isOpen(id)
	const abortExport = useCallback(() => exportController.current?.abort(), [])

	useEffect(() => {
		if (!isModalOpen) {
			abortExport()
		}
		return abortExport
	}, [abortExport, isModalOpen])

	const hide = useCallback(() => {
		abortExport()
		hideModal(id)
	}, [abortExport, hideModal, id])

	const startExport = (request: AddMarketExportBody, timeFrame?: ExportTimeFrame) => {
		abortExport()
		const controller = new AbortController()
		exportController.current = controller
		setIsLoading(true)
		setError(null)
		trackEvent({
			action: request.exportType === "DELIVERED_INVENTORY" ? "marketExportDelivered" : "marketExport",
			category: "dealership_list",
			clickElementDetails: timeFrame
				? [request.locale, TIME_FRAME_TRACKING_VALUES[timeFrame]].join("|")
				: request.locale,
			name: marketplaceKey,
		})
		onExport(request, setProgress, controller.signal)
			.then(() => {
				if (!controller.signal.aborted) {
					hide()
				}
			})
			.catch(() => {
				if (!controller.signal.aborted) {
					setError(formatMessage(messages.marketExportError))
				}
			})
			.finally(() => {
				if (!controller.signal.aborted) {
					setIsLoading(false)
					setProgress(undefined)
				}
			})
	}

	return (
		<Modal id={id} heading={formatMessage(marketExportMessages.marketExport)} width="wide" onDismiss={abortExport}>
			<MarketExportModalContent
				error={error}
				isLoading={isLoading}
				progress={progress}
				today={today}
				view={view}
				onCancel={hide}
				onSubmit={startExport}
			/>
		</Modal>
	)
}
